/**
 * CompetitiveLandscapeRenderer (T-058, ARCH-005 §1/§9, TECH-09, AI-01).
 *
 * Renders the Zod-validated competitive_landscape payload: a summary, a
 * per-competitor card stack, an N-dimension comparison matrix table, and a
 * 2-axis (NxN) positioning grid plotted from `positioning_grid`. Body only;
 * meta-note + Sources + video are DeliverableLayout's job (ARCH-005 §3).
 *
 * Verbosity (ARCH-005 §5): 'recommend' collapses the per-competitor breakdown
 * and the matrix to a one-line count; 'show_me' shows everything;
 * 'build_it_with_me' adds the methodology callout.
 */

import { Markdown } from '../Markdown';
import {
  FieldList,
  MethodologyCallout,
  SectionHead,
  type RendererProps,
} from '../shared';
import type { CompetitiveLandscapeContent } from './payload';

export function CompetitiveLandscapeRenderer({
  payload,
  verbosity,
}: RendererProps<CompetitiveLandscapeContent>) {
  const full = verbosity !== 'recommend';
  return (
    <div className="space-y-6">
      <div>
        <SectionHead>Competitive landscape</SectionHead>
        <Markdown source={payload.summary_md} />
      </div>

      {full ? (
        <div className="space-y-4">
          {payload.competitors.map((c, i) => (
            <div
              key={i}
              className="rounded-md border border-[color:var(--color-rule)] p-4"
            >
              <p className="font-semibold text-[color:var(--color-navy)]">
                {c.name}
              </p>
              <p className="mb-2 text-sm text-[color:var(--color-text-mute)]">
                {c.what_they_do}
              </p>
              {c.strengths.length > 0 ? (
                <p className="text-sm text-[color:var(--color-navy)]">
                  <span className="font-medium">Strengths: </span>
                  {c.strengths.join(', ')}
                </p>
              ) : null}
              {c.weaknesses.length > 0 ? (
                <p className="text-sm text-[color:var(--color-navy)]">
                  <span className="font-medium">Weaknesses: </span>
                  {c.weaknesses.join(', ')}
                </p>
              ) : null}
              {c.positioning_note ? (
                <p className="mt-1 text-sm text-[color:var(--color-text-mute)]">
                  {c.positioning_note}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[color:var(--color-text-mute)]">
          {payload.competitors.length} competitors analyzed. Switch coaching
          depth to see the full breakdown.
        </p>
      )}

      {full && payload.matrix ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border-b border-[color:var(--color-rule)] p-2 text-left">
                  Competitor
                </th>
                {payload.matrix.dimensions.map((d, i) => (
                  <th
                    key={i}
                    className="border-b border-[color:var(--color-rule)] p-2 text-left"
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payload.matrix.rows.map((r, i) => (
                <tr key={i}>
                  <td className="border-b border-[color:var(--color-rule)] p-2 font-medium">
                    {r.competitor}
                  </td>
                  {r.values.map((v, j) => (
                    <td
                      key={j}
                      className="border-b border-[color:var(--color-rule)] p-2"
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {full && payload.positioning_grid ? (
        <PositioningGrid grid={payload.positioning_grid} />
      ) : null}

      <MethodologyCallout verbosity={verbosity}>
        <p>
          Start from the axes that matter to a buyer, not the ones that flatter
          you. Plot honestly, then ask where the open space is. The empty
          quadrant is the position.
        </p>
      </MethodologyCallout>
    </div>
  );
}

/**
 * 2-axis positioning grid (T-058 AC: "a 2x2 (or NxN) positioning grid").
 * Points carry x and y in any numeric range; we normalize to 0..1 against the
 * observed min/max so the grid renders regardless of the scale the agent used.
 */
function PositioningGrid({
  grid,
}: {
  grid: NonNullable<CompetitiveLandscapeContent['positioning_grid']>;
}) {
  const xs = grid.points.map((p) => p.x);
  const ys = grid.points.map((p) => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);

  const norm = (v: number, lo: number, hi: number) =>
    hi === lo ? 0.5 : (v - lo) / (hi - lo);

  return (
    <div>
      <SectionHead>Positioning</SectionHead>
      <div className="relative aspect-square w-full max-w-md rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-white)]">
        <div className="absolute inset-y-0 left-1/2 w-px bg-[color:var(--color-rule)]" />
        <div className="absolute inset-x-0 top-1/2 h-px bg-[color:var(--color-rule)]" />
        <span className="absolute -bottom-6 left-0 text-xs text-[color:var(--color-text-mute)]">
          {grid.x_axis} (low)
        </span>
        <span className="absolute -bottom-6 right-0 text-xs text-[color:var(--color-text-mute)]">
          {grid.x_axis} (high)
        </span>
        <span className="absolute -left-2 bottom-0 origin-bottom-left -rotate-90 text-xs text-[color:var(--color-text-mute)]">
          {grid.y_axis} (low)
        </span>
        <span className="absolute -left-2 top-0 origin-top-left -rotate-90 text-xs text-[color:var(--color-text-mute)]">
          {grid.y_axis} (high)
        </span>
        {grid.points.map((p, i) => {
          const left = norm(p.x, xMin, xMax) * 100;
          const bottom = norm(p.y, yMin, yMax) * 100;
          return (
            <div
              key={i}
              className="absolute -translate-x-1/2 translate-y-1/2"
              style={{ left: `${left}%`, bottom: `${bottom}%` }}
            >
              <span className="block h-2 w-2 rounded-full bg-[color:var(--color-rose)]" />
              <span className="mt-1 block whitespace-nowrap text-xs text-[color:var(--color-navy)]">
                {p.label}
              </span>
            </div>
          );
        })}
      </div>
      <div className="h-6" />
      <FieldList
        title="Plotted"
        items={grid.points.map((p) => p.label)}
      />
    </div>
  );
}
